import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { DecisionTrack } from "@/constants/decision-track";
import { DivergenceStatusEnum } from "@/enum/divergence-status";
import {
  useBatchUpdateDivergenceStatusMutation,
  useUpdateDivergenceStatusMutation,
} from "@/services/divergence";
import { JSX, useState } from "react";

import { BatchRow, useBatchApproval } from "../../hooks/use-batch-approval";
import { BATCH_APPROVAL_TEST_ID } from "../../utils/batch-approval-test-ids";
import { TrackedSelection, trackSelection } from "../../utils/batch-tracks";
import { apiErrorMessage } from "../../utils/api-error-message";
import { onlyDigits } from "../../utils/manual-identity";
import { BATCH_APPROVAL_LABELS, DECISION_LABELS } from "../../utils/messages";
import { rememberOwnDecisions } from "../../utils/own-decisions";
import { TRACK_SPEC } from "../../utils/track-spec";
import { BatchItem } from "../shared/batch-action-modal";
import { BatchApprovalModalProps } from "./batch-approval-types";
import { BatchTrackSection } from "./batch-track-section";

const SINGLE_TRACK: number = 1;

const isInformed = (row: BatchRow): boolean =>
  Boolean(row.state.informed) || Boolean(row.state.chosen);

const introOf = (
  tracked: TrackedSelection<BatchItem>,
  total: number
): string => {
  if (tracked.present.length <= SINGLE_TRACK) {
    return BATCH_APPROVAL_LABELS.INTRO(total);
  }

  const summaries: string[] = tracked.present.map((track: DecisionTrack): string =>
    TRACK_SPEC[track].summaryOf(tracked.groups[track].length)
  );

  return BATCH_APPROVAL_LABELS.SPLIT_INTRO(total, summaries);
};

export function BatchApprovalModal({
  items,
  blockedCount = 0,
  onFinished,
}: BatchApprovalModalProps): JSX.Element {
  const tracked: TrackedSelection<BatchItem> = trackSelection(items);
  const approval = useBatchApproval(tracked.identityIds);

  const [updateStatus] = useUpdateDivergenceStatusMutation();
  const [batchUpdate] = useBatchUpdateDivergenceStatusMutation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const rows: BatchRow[] = [...approval.rows.values()];
  const ready: BatchRow[] = rows.filter((row: BatchRow): boolean => row.ready);
  const readyCount: number = ready.length + tracked.selfReadyIds.length;
  const pending: number = items.length - readyCount;

  const submit = async (): Promise<void> => {
    setIsSubmitting(true);

    const informed: BatchRow[] = ready.filter(isInformed);
    const plain: BatchRow[] = ready.filter(
      (row: BatchRow): boolean => !isInformed(row)
    );

    rememberOwnDecisions([
      ...tracked.selfReadyIds,
      ...ready.map((row: BatchRow): number => row.id),
    ]);

    try {
      const batchItems = [
        ...tracked.selfReadyIds.map((id: number) => ({
          id,
          status: DivergenceStatusEnum.APPROVED,
        })),
        ...plain.map((row: BatchRow) => ({
          id: row.id,
          status: DivergenceStatusEnum.APPROVED,
          installments_count: row.state.installments,
        })),
      ];

      if (batchItems.length > 0) {
        await batchUpdate({ items: batchItems }).unwrap();
      }

      for (const row of informed) {
        await updateStatus({
          id: row.id,
          status: DivergenceStatusEnum.APPROVED,
          installments_count: row.state.installments,
          ...(row.state.chosen
            ? { manual_identity_operator_code: row.state.chosen }
            : { manual_identity_cpf: onlyDigits(row.state.cpf) }),
        }).unwrap();
      }

      toast({
        variant: "success",
        description: DECISION_LABELS.SUCCESS_APPROVE,
        duration: 3000,
      });

      onFinished();
    } catch (error) {
      toast({
        variant: "destructive",
        description: apiErrorMessage(error) ?? DECISION_LABELS.ERROR,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      data-testid={BATCH_APPROVAL_TEST_ID}
      className="mx-auto flex max-h-[70vh] w-full max-w-2xl flex-col gap-4"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted-foreground">
          {introOf(tracked, items.length)}
        </p>
        {blockedCount > 0 && (
          <p className="text-sm text-amber-700">
            {BATCH_APPROVAL_LABELS.BLOCKED(blockedCount)}
          </p>
        )}
        {Boolean(approval.loadError) && (
          <p role="alert" className="text-sm text-destructive">
            {approval.loadError}
          </p>
        )}
      </div>

      {approval.isLoading && (
        <div
          role="status"
          aria-label={BATCH_APPROVAL_LABELS.LOADING}
          className="h-32 w-full animate-pulse rounded-md border bg-muted/40"
        />
      )}

      <div className="flex flex-col gap-5 overflow-y-auto pr-1">
        {tracked.present.map(
          (track: DecisionTrack): JSX.Element => (
            <BatchTrackSection
              key={track}
              track={track}
              items={tracked.groups[track]}
              approval={approval}
            />
          )
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t pt-3">
        <span className="text-sm text-muted-foreground">
          {BATCH_APPROVAL_LABELS.READY(readyCount, items.length)}
        </span>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onFinished}>
            {BATCH_APPROVAL_LABELS.CANCEL}
          </Button>
          <Button
            type="button"
            disabled={readyCount === 0 || isSubmitting}
            onClick={(): void => void submit()}
          >
            {isSubmitting
              ? DECISION_LABELS.PROCESSING
              : BATCH_APPROVAL_LABELS.CONFIRM(readyCount)}
          </Button>
        </div>
      </div>

      {pending > 0 && (
        <p className="text-xs text-amber-700">
          {BATCH_APPROVAL_LABELS.PARTIAL(pending)}
        </p>
      )}
    </div>
  );
}
